import React, { useState, useEffect } from 'react';
import {
  BriefcaseIcon,
  ArrowLeftIcon,
  EyeIcon,
  CheckCircleIcon,
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyYenIcon,
  MapPinIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { API_BASE } from '../../lib/config';

const QuickJobApprovals = ({ onBack }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, month_total: 0 });

  useEffect(() => {
    fetchPendingJobs();
    fetchStats();
  }, [statusFilter]);

  const fetchPendingJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const pending = data.filter(j => j.status === 'pending');
        setJobs(pending);
      } else {
        setJobs([]);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('求人情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/admin/jobs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (e) {
      console.error('Error fetching job stats:', e);
    }
  };

  const handleJobApproval = async (jobId, action, notes = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      const statusMap = {
        approve: 'active', // Approved jobs become active
        reject: 'rejected',
        request_changes: 'draft' // Send back to draft?
      };

      const response = await fetch(`${API_BASE}/admin/jobs/${jobId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: statusMap[action],
          admin_note: notes
        })
      });

      if (!response.ok) throw new Error('API Error');

      toast.success(`求人を${action === 'approve' ? '承認' : '却下'}しました`);
      fetchPendingJobs(); // データを再取得
      fetchStats(); // 統計も更新
    } catch (error) {
      toast.error('操作に失敗しました');
    }
  };

  const getCategoryBadge = (category) => {
    const categories = {
      food_service: { class: 'bg-orange-100 text-orange-800', label: '飲食' },
      event: { class: 'bg-purple-100 text-purple-800', label: 'イベント' },
      office_work: { class: 'bg-blue-100 text-blue-800', label: '事務' },
      retail: { class: 'bg-green-100 text-green-800', label: '小売' },
      cleaning: { class: 'bg-yellow-100 text-yellow-800', label: '清掃' }
    };
    const badge = categories[category] || { class: 'bg-gray-100 text-gray-800', label: 'その他' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            ダッシュボードに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <BriefcaseIcon className="w-8 h-8 text-green-600 mr-3" />
            求人承認管理
          </h2>
          <p className="text-gray-600 mt-1">事業者から投稿された求人の承認・却下</p>
        </div>

        {/* ステータスフィルター */}
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700">ステータス:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="pending">承認待ち</option>
            <option value="approved">承認済み</option>
            <option value="rejected">却下</option>
          </select>
        </div>
      </div>

      {/* 統計カード */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">承認待ち</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">承認済み</p>
              <p className="text-2xl font-bold text-gray-900">{stats.approved}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XMarkIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">却下</p>
              <p className="text-2xl font-bold text-gray-900">{stats.rejected}件</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">今月の投稿</p>
              <p className="text-2xl font-bold text-gray-900">{stats.month_total}件</p>
            </div>
          </div>
        </div>
      </div>

      {/* 求人一覧 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">求人一覧</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-500">読み込み中...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    求人情報
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    事業者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    カテゴリ・時給
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    勤務情報
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    応募状況
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    投稿日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{job.title}</div>
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {job.description}
                        </div>
                        <div className="mt-1 flex items-center text-xs text-gray-400">
                          <MapPinIcon className="w-3 h-3 mr-1" />
                          {job.location}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{job.employer_name}</div>
                      <div className="text-xs text-gray-500">{job.employer_id}</div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {getCategoryBadge(job.category)}
                        <div className="flex items-center text-sm text-gray-900">
                          <CurrencyYenIcon className="w-4 h-4 mr-1" />
                          {job.hourly_rate.toLocaleString()}/時
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center">
                          <CalendarIcon className="w-4 h-4 mr-1" />
                          {formatDate(job.work_date)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{job.work_time}</div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {job.current_applicants}/{job.max_applicants}人
                      </div>
                      <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${(job.current_applicants / job.max_applicants) * 100}%` }}
                        ></div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(job.submitted_at)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => alert(`求人ID: ${job.id}の詳細を表示`)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          詳細
                        </button>
                        <button
                          onClick={() => handleJobApproval(job.id, 'approve')}
                          className="text-green-600 hover:text-green-900 flex items-center"
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          承認
                        </button>
                        <button
                          onClick={() => handleJobApproval(job.id, 'reject')}
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <XMarkIcon className="w-4 h-4 mr-1" />
                          却下
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {jobs.length === 0 && !loading && (
          <div className="p-8 text-center">
            <BriefcaseIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">該当する求人がありません</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickJobApprovals;